import { ChangeDetectorRef, Component, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { environment } from 'projects/wallet-qris-transaction-webview/src/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { EncryptService } from '../../core/services/encrypt/encrypt.service';
import { QrisPaymentPayload } from '../../core/usecase/fs-wallet-qris/interface/qris-payment.dto';
import { QrisPaymentUsecase } from '../../core/usecase/fs-wallet-qris/qris-payment.usecase';
import { RequestPinUsecase } from '../../core/usecase/fs-wallet-qris/request-pin.usecase';
import { ValidatePinUsecase } from '../../core/usecase/fs-wallet-qris/validate-pin.usecase';

@Component({
  selector: 'wallet-qris-trx-pin',
  templateUrl: './wallet-qris-trx-pin.component.html',
  styleUrls: ['./wallet-qris-trx-pin.component.scss'],
  //   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletQrisTrxPinComponent {
  reloadPin = new EventEmitter<void>();
  phone: string = '';
  data: any = null;
  lang: string = 'id';

  isLoading: boolean = false;
  isOtpLoading: boolean = false;
  isPinLocked = false;
  pinAuthKey: string = '';
  paymentError = false;
  paymentErrorCode = '';
  otpCode: string = '';
  entry_point: string = '';
  requestId: string = '';
  token: string = '';
  token_data: any = null;
  destroyed$ = new Subject();
  public timeLimitInSecond: 300 | 0 = 300;
  errorMessageShown: boolean = false;
  errorMessageContent: string = '';
  pinError: string = '';
  pinDisabled: boolean = true;
  contactCSUrl = 'https://api.whatsapp.com/send?phone=6285219526186&text=Halo%20tim%20CS%20Paper.id%2C%20saya%20memiliki%20pertanyaan%20tentang%20Paper%20Horizon%20Wallet'

  private readonly ngUnsubsribe$ = new Subject<void>();

  private readonly OTP_LENGTH = 6;

  get isPaymentError() {
    return this.paymentError;
  }

  config = {
    allowNumbersOnly: true,
    isPasswordInput: true,
    length: this.OTP_LENGTH,
  };

  constructor(
    private readonly activeRouter: ActivatedRoute,
    private readonly requestPinUsecase: RequestPinUsecase,
    private readonly validatePinUsecase: ValidatePinUsecase,
    private readonly qrisPaymentUsecase: QrisPaymentUsecase,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly encryptService: EncryptService,
    private readonly cdr: ChangeDetectorRef,
    public router: Router
  ) {
    this.token =
      this.router.getCurrentNavigation()?.extras?.state?.['token'] || {};
    this.data =
      this.router.getCurrentNavigation()?.extras?.state?.['data'] || {};
    this.token_data =
      this.router.getCurrentNavigation()?.extras?.state?.['token_data'] || {};
    this.activeRouter.queryParamMap.subscribe((params) => {
      this.lang = params.get('lang')!;
    });
  }

  ngOnInit(): void {
    this.requestPin();
  }

  onPinChange(pin: string): void {
    if (pin.length === this.OTP_LENGTH) {
      this.onPinCompleted(pin);
    }
  }

  getJwtPayload(t: string) {
    let payload;
    try {
      payload = JSON.parse(window.atob(t.split('.')[1]));
    } catch (error) {
      // do nothing
      return null;
    }
    return payload;
  }

  onPinCompleted(pin: string): void {
    const payload = {
      request_id: this.requestId,
      pin: pin,
    };
    if (!this.isOtpLoading) {
      this.validatePin(payload);
    }
  }

  requestPin(pin?: string) {
    this.clear();
    if(!pin) {
      this.reloadPin.emit();
    }
    this.isOtpLoading = true;
    this.requestPinUsecase
      .execute({
        wallet_id: this.token_data?.wallet_id,
        company_id: this.token_data?.client_ref_id,
        user_id: this.token_data?.user_ref_id,
        token: this.token,
      })
      .pipe(takeUntil(this.ngUnsubsribe$))
      .subscribe({
        next: (res) => {
          this.timeLimitInSecond = 300;
          this.pinDisabled = false;
          this.isOtpLoading = false;
          this.requestId = res.data?.request_id || '';
          if(pin) {
            this.validatePin({
              request_id: this.requestId,
              pin: pin,
            });
          }
        },
        error: (err) => {
          if (err.status === 429 || err.status === 423) {
            const detail = this.translateService.instant(
              'messages.error'
            );
            this.messageService.add({
              severity: 'error',
              detail: detail,
            });
            this.pinDisabled = true;
          } else {
            const error_detail =
              this.translateService.instant('messages.error');
            this.messageService.add({
              severity: 'error',
              detail: error_detail,
            });
            this.pinDisabled = true;
          }
          this.isOtpLoading = false;
        },
      });
    this.timeLimitInSecond = 300;
  }

  validatePin(payload: {pin: string, request_id: string}) {
    this.clear();
    this.isOtpLoading = true;
    this.isLoading = true;
    const data = this.generatePinPayload(payload);
    this.validatePinUsecase
      .execute(data)
      .pipe(takeUntil(this.ngUnsubsribe$))
      .subscribe({
        next: (res) => {
          this.isOtpLoading = false;
          this.isLoading = false;
          this.pinAuthKey = res.data?.auth_key || '';
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isOtpLoading = false;
          this.isLoading = false;
          this.pinAuthKey = '';

          if (err.status === 429 || err.status === 423) {
            this.isPinLocked = true;
            this.isOtpLoading = false;
          } else if (err.status === 422 && err.error.error_code === 'INVALID_PIN') {
            this.pinError = this.translateService.instant(
              'wallet_trx_pin.error_invalid'
            );
            this.isOtpLoading = false;
          } else if ((err.status === 400 || err.status === 401) && err.error.error_code === 'PIN_EXPIRED') {
            this.requestPin(payload.pin);
          } else {
            const error_detail =
              this.translateService.instant('messages.error');
            this.messageService.add({
              severity: 'error',
              detail: error_detail,
              life: 5000,
            });
          }

          this.cdr.detectChanges();
        },
      });
  }

  executePayment() {
    this.isOtpLoading = true;
    this.isLoading = true;
    const data = this.generatePaymentPayload(this.data);
    console.log(data);
    this.qrisPaymentUsecase
      .execute(data)
      .pipe(takeUntil(this.ngUnsubsribe$))
      .subscribe({
        next: (res) => {
          this.isOtpLoading = false;
          this.isLoading = false;
          this.router.navigate(['trx-status'], {
            state: {
              token: this.token,
              data: res.data,
              token_data: this.token_data
            },
            queryParams: { lang: this.lang },
            skipLocationChange: true,
          });
        },
        error: (err) => {
          this.paymentErrorCode = err.status;
          this.paymentError = true;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  handlePayment(){
    if(this.data) {
      this.executePayment();
    }
  }

  generatePinPayload(payload: {pin: string, request_id: string}) {
    const encryptedPayload = this.encryptData(payload);
    return {
      payload: payload,
      wallet_id: this.token_data?.wallet_id,
      company_id: this.token_data?.client_ref_id,
      user_id: this.token_data?.user_ref_id,
      token: this.token,
      ...encryptedPayload
    };
  }

  generatePaymentPayload(payload: QrisPaymentPayload) {
    const encryptedPayload = this.encryptData(payload);
    return {
      payload: payload,
      wallet_id: this.token_data?.wallet_id,
      company_id: this.token_data?.client_ref_id,
      user_id: this.token_data?.user_ref_id,
      token: this.token,
      auth_key: this.pinAuthKey,
      ...encryptedPayload
    };
  }

  clear() {
    this.messageService.clear();
    this.pinError = '';
    this.pinAuthKey = '';
    this.paymentError = false;
    this.isPinLocked = false;
  }

  encryptData(data: any) {
    const result = this.encryptService.encryptHybridPayload(data, environment.public_pem);
    return result
  }

  getHelp() {
    window.location.href = this.contactCSUrl;
  }
}
